import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ArrowLeft, ChevronDown, Loader2, Plane, Users, Luggage, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { vehicleClasses } from '../utils/mockData';
import { capitalizeName } from '../lib/names';
import { greetingText } from '../lib/greeting';
import { isWhereNext } from '../lib/nextStep';
import { createQuoteClient, UNAVAILABLE } from '../lib/quoteClient';
import { maxParty, nextFit, fitMessage } from '../lib/classFit';
import PreferencesCard from './PreferencesCard';
import { bizApi, businessBody } from '../lib/business';
import RiderNoteCard from './RiderNoteCard';
import OurStandards from './OurStandards';
import { noteProblem } from '../lib/riderNote';
import PreviewFooter from './PreviewFooter';
import { prefsFromProfile, prefsForBooking } from '../lib/cabin';
import GooglePlacesInput from './GooglePlacesInput';
import GoogleMapsService from '../services/GoogleMapsService';
import { SignPreview } from './MeetGreetPanel';
import MeetGreetPanel from './MeetGreetPanel';
import { fetchAirportAt, passengerProblem, phoneProblem } from '../lib/airportBooking';
import { fetchMeetingPoint, signNameProblem, tidyName, defaultSignName } from '../lib/meetGreet';
import {
  MODES, HOURS_MIN, HOURS_MAX, SEAT_MAX, nyNow, partsFromPicker, whenLabel, classesFor, effectiveHours,
  readyForClasses, quoteBody, checkoutBody, quoteTotal, quoteBreakdown, steps, splitDropoffNote,
} from '../lib/bookPreview';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = 'https://customer-assets.emergentagent.com/job_buraq-transport/artifacts/0a9wh5vw_0E081036-6309-4011-9127-0A9FE497D0F8.png';
const WIDE = '(min-width: 1024px)';
const PHONE = '(max-width: 767px)';
const terminalName = (airport, code) => { const t = ((airport && airport.terminals) || []).find((x) => x.code === code); return t ? t.name : `Terminal ${code}`; };

function useWide(query = WIDE) {
  const get = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(query).matches;
  const [wide, setWide] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const m = window.matchMedia(query);
    const on = () => setWide(m.matches);
    on();
    if (m.addEventListener) m.addEventListener('change', on); else m.addListener(on);
    return () => { if (m.removeEventListener) m.removeEventListener('change', on); else m.removeListener(on); };
  }, [query]);
  return wide;
}
const reduced = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const token = () => localStorage.getItem('rideburaq_token');

// Smooth reveal: fades and rises into place after `delay` ms (no motion for people who asked for less).
function Reveal({ show = true, delay = 0, children, className = '' }) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!show) { setOn(false); return undefined; }
    if (reduced()) { setOn(true); return undefined; }
    const t = setTimeout(() => setOn(true), delay + 30);
    return () => clearTimeout(t);
  }, [show, delay]);
  if (!show) return null;
  return (
    <div className={className} style={{ opacity: on ? 1 : 0, transform: on ? 'none' : 'translateY(16px)', transition: reduced() ? 'none' : 'opacity 650ms ease, transform 650ms cubic-bezier(.2,.8,.2,1)' }}>
      {children}
    </div>
  );
}

// The hero photo (NYC skyline, Brooklyn Bridge, the RideBuraq car on the right): decorative, behind the greeting and the address fields ONLY.
// WebP first with a JPG fallback, a phone crop below 768px, srcset for the sizes; dark overlays keep every text AA and fade into the solid page colour.
function HeroBackdrop({ phone }) {
  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true" data-testid="hero-image">
      <picture>
        <source media="(max-width: 767px)" type="image/webp" srcSet="/images/booking-hero-m-480.webp 480w, /images/booking-hero-m-800.webp 800w" sizes="100vw" />
        <source media="(max-width: 767px)" type="image/jpeg" srcSet="/images/booking-hero-m-480.jpg 480w, /images/booking-hero-m-800.jpg 800w" sizes="100vw" />
        <source type="image/webp" srcSet="/images/booking-hero-1024.webp 1024w, /images/booking-hero-1600.webp 1600w" sizes="100vw" />
        <img src="/images/booking-hero-1600.jpg" srcSet="/images/booking-hero-1024.jpg 1024w, /images/booking-hero-1600.jpg 1600w" sizes="100vw" width="1600" height="900"
          alt="" decoding="async" fetchpriority="high" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: phone ? '82% 70%' : '50% 72%' }} />
      </picture>
      {/* strongest behind the text (left), clear on the right where the car is; on phones a flat dark tint from top to bottom */}
      <div className="absolute inset-0 md:hidden" style={{ background: 'linear-gradient(180deg, rgba(13,14,17,0.72) 0%, rgba(13,14,17,0.62) 55%, rgba(13,14,17,0.38) 100%)' }} />
      <div className="absolute inset-0 hidden md:block" style={{ background: 'linear-gradient(90deg, rgba(13,14,17,0.80) 0%, rgba(13,14,17,0.72) 38%, rgba(13,14,17,0.32) 62%, rgba(13,14,17,0.00) 100%)' }} />
      {/* fades smoothly into the solid page colour before the car classes */}
      <div className="absolute inset-x-0 bottom-0 h-40 lg:h-48" style={{ background: 'linear-gradient(180deg, rgba(13,14,17,0) 0%, #0d0e11 100%)' }} />
    </div>
  );
}

const fieldCls = 'w-full bg-transparent border-0 h-16 px-0 text-lg sm:text-xl text-gray-50 placeholder:text-gray-300 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none';
const roundBtn = 'w-11 h-11 rounded-full border border-white/25 text-xl leading-none text-white hover:bg-white/[0.08] hover:border-white/60 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:border-white/25 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/70';
const lineInput = 'w-full rounded-2xl bg-white/[0.04] border border-white/15 px-5 h-16 text-lg text-gray-50 placeholder:text-gray-400 focus:border-white/70 focus:outline-none';
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// A stepper in its own row: the label on the left, a pill on the right with a thin round button, a clear number and a thin round button, each with room around it.
function Stepper({ label, icon: Icon, value, min, max, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-full border border-white/15 bg-white/[0.03] pl-6 pr-2.5 py-2.5" data-testid={testid}>
      <span className="flex items-center gap-3 text-base text-gray-100">{Icon && <Icon className="h-4 w-4 text-gray-300" aria-hidden="true" />}{label}</span>
      <span className="inline-flex items-center gap-2 rounded-full border border-white/15 p-1">
        <button type="button" aria-label={`Fewer ${label}`} disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))} className={roundBtn}>−</button>
        <span className="min-w-[3rem] text-center text-xl font-medium text-white tabular-nums" data-testid={`${testid}-value`} aria-live="polite">{value}</span>
        <button type="button" aria-label={`More ${label}`} disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))} className={roundBtn}>+</button>
      </span>
    </div>
  );
}

const BookPreview = ({ business = null }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { toast } = useToast();

  // ---- the ride ----
  const wide = useWide();
  const phone = useWide(PHONE);
  const [mode, setMode] = useState(MODES.RIDE);
  const [pickup, setPickup] = useState({ address: '', lat: null, lng: null, committed: false });
  const [dropoff, setDropoff] = useState({ address: '', lat: null, lng: null, committed: false });
  const [hours, setHours] = useState(HOURS_MIN);
  const [when, setWhen] = useState(null);                       // null = Now, else { date, time }
  const [whenDate, setWhenDate] = useState(null);
  const [showWhen, setShowWhen] = useState(false);
  const [pickupAirport, setPickupAirport] = useState(null);
  const [dropoffAirport, setDropoffAirport] = useState(null);

  // ---- airport details ----
  const [flightNumber, setFlightNumber] = useState('');
  const [terminal, setTerminal] = useState('');
  const [terminalFromFlight, setTerminalFromFlight] = useState(false);
  const [flightState, setFlightState] = useState('idle');       // idle | looking | found | missing
  const [showChips, setShowChips] = useState(false);
  const [dropNote, setDropNote] = useState('');

  // ---- prices and choices ----
  const [quotes, setQuotes] = useState({});                      // { classId: { status, quote, message } }
  const [selected, setSelected] = useState('');
  const [seats, setSeats] = useState(1);
  const [meet, setMeet] = useState(false);                       // false = Curbside
  const [signName, setSignName] = useState(null);                // null = the default
  const [someoneElse, setSomeoneElse] = useState(false);
  const [travelerId, setTravelerId] = useState(business && business.travelers.length ? '' : 'new');   // business mode: a saved traveler's id, or 'new'
  const [newT, setNewT] = useState({ name: '', mobile: '', email: '', save: true });
  const [paxName, setPaxName] = useState('');
  const [paxPhone, setPaxPhone] = useState('');
  const [passengers, setPassengers] = useState(1);
  const [bags, setBags] = useState(0);
  const [prefs, setPrefs] = useState(() => prefsFromProfile(currentUser && currentUser.preferences));
  const [riderNote, setRiderNote] = useState('');
  const [noteChips, setNoteChips] = useState([]);
  const [keepFit, setKeepFit] = useState('');                    // the fit suggestion the rider chose to keep
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [finalQuote, setFinalQuote] = useState(null);
  const [finalState, setFinalState] = useState('idle');          // idle | loading | ok | error
  const [finalMessage, setFinalMessage] = useState('');
  const [policy, setPolicy] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const classesRef = useRef(null);
  const detailsRef = useRef(null);
  const finalId = useRef(0);

  const isHourly = mode === MODES.HOURLY;
  const list = useMemo(() => classesFor(mode, vehicleClasses), [mode]);
  const cls = useMemo(() => vehicleClasses.find((c) => c.id === selected) || null, [selected]);
  const sections = steps({ mode, pickup, dropoff, selectedClass: selected, pickupAirport });
  const party = useMemo(() => maxParty(list), [list]);
  const better = cls ? nextFit(list, cls.id, passengers, bags) : null;
  const fitKey = better ? `${cls.id}:${passengers}:${bags}` : '';
  // the next step: once the pickup (and the terminal, at an airport) is set, "Where to?" is highlighted and focused
  const nextIsWhere = isWhereNext({ hourly: mode === MODES.HOURLY, pickup, dropoff, pickupAirport, terminal });
  useEffect(() => {
    if (!nextIsWhere) return;
    const el = document.querySelector('[data-testid="preview-dropoff"]');
    if (el && typeof el.focus === 'function') el.focus({ preventScroll: false });
  }, [nextIsWhere]);
  const [greetHour] = useState(() => new Date().getHours()); // the viewer's local time when the page opens

  const savedT = business && travelerId && travelerId !== 'new' ? business.travelers.find((t) => t.id === travelerId) : null;
  const bizName = business ? (savedT ? (savedT.signName || savedT.name) : newT.name) : '';
  const signDefault = business ? (tidyName(bizName) ? capitalizeName(tidyName(bizName)) : '') : someoneElse && tidyName(paxName) ? capitalizeName(tidyName(paxName)) : defaultSignName(currentUser);
  const sched = when || nyNow();
  const lookupDate = `${sched.date}T${sched.time}:00`;

  // the cancellation policy in words, from the server
  useEffect(() => {
    fetch(`${API}/api/cancellation-policy`).then((r) => (r.ok ? r.json() : null)).then(setPolicy).catch(() => {});
  }, []);

  // airports are found by place, in any mode
  useEffect(() => {
    let alive = true;
    fetchAirportAt(API, token(), pickup.lat, pickup.lng).then((a) => { if (alive) setPickupAirport(a); }).catch(() => { if (alive) setPickupAirport(null); });
    return () => { alive = false; };
  }, [pickup.lat, pickup.lng]);
  useEffect(() => {
    let alive = true;
    fetchAirportAt(API, token(), dropoff.lat, dropoff.lng).then((a) => { if (alive) setDropoffAirport(a); }).catch(() => { if (alive) setDropoffAirport(null); });
    return () => { alive = false; };
  }, [dropoff.lat, dropoff.lng]);
  useEffect(() => { setTerminal(''); setTerminalFromFlight(false); setFlightState('idle'); setShowChips(false); setMeet(false); }, [pickupAirport && pickupAirport.code]);

  // the flight fills the terminal; if it cannot, the chips appear
  useEffect(() => {
    const fn = flightNumber.replace(/\s+/g, '');
    if (!pickupAirport || fn.length < 3 || pickup.lat == null) { setFlightState('idle'); return undefined; }
    let alive = true;
    setFlightState('looking');
    const t = setTimeout(() => {
      fetchMeetingPoint(API, token(), { lat: pickup.lat, lng: pickup.lng, flightNumber: fn, date: lookupDate }).then((p) => {
        if (!alive) return;
        if (p && p.terminalSource === 'flight' && p.terminal) { setTerminal(p.terminal); setTerminalFromFlight(true); setFlightState('found'); }
        else { setFlightState(p && (p.flightLookup === 'unavailable' || p.flightLookup === 'off') ? 'unavailable' : (p && p.flightLookup === 'ok' ? 'noterminal' : 'missing')); setTerminalFromFlight(false); setShowChips(true); }
      }).catch(() => { if (alive) { setFlightState('missing'); setShowChips(true); } });
    }, 650);
    return () => { alive = false; clearTimeout(t); };
  }, [flightNumber, pickupAirport, pickup.lat, pickup.lng, lookupDate]);

  // the real price of every class, from the quote route (the same route and rules as the current page). One request at a time, with a time limit,
  // one retry, and answers reused, so a slow or rate-limited moment never leaves a tile spinning.
  const quoteClient = useMemo(() => createQuoteClient({ api: API, getToken: token }), []);
  const tileBody = (c) => quoteBody({ cls: c, mode, hours, seats: mode === MODES.FAMILY ? 1 : 0, pickup, dropoff, when, meetGreet: false, pickupAirport, dropoffAirport });
  const asTile = (res) => (res.ok ? { status: 'ok', quote: res.quote } : { status: 'error', message: res.message || UNAVAILABLE, retryable: res.retryable !== false });

  const quoteKey = JSON.stringify([mode, pickup.address, pickup.lat, pickup.lng, dropoff.address, dropoff.lat, dropoff.lng, isHourly ? hours : 0, when, !!pickupAirport, !!dropoffAirport]);
  useEffect(() => {
    if (!readyForClasses({ mode, pickup, dropoff })) { setQuotes({}); return undefined; }
    const ctl = new AbortController();
    setQuotes(Object.fromEntries(list.map((c) => [c.id, { status: 'loading' }])));
    const t = setTimeout(() => {
      quoteClient.quoteEach(list.map((c) => ({ id: c.id, body: tileBody(c) })), (id, res) => setQuotes((prev) => ({ ...prev, [id]: asTile(res) })), ctl.signal);
    }, 450);
    return () => { clearTimeout(t); ctl.abort(); };
  // eslint-disable-next-line
  }, [quoteKey, list]);

  const retryTile = async (c) => {
    setQuotes((prev) => ({ ...prev, [c.id]: { status: 'loading' } }));
    const res = await quoteClient.quote(tileBody(c));
    setQuotes((prev) => ({ ...prev, [c.id]: asTile(res) }));
  };

  // the total for the chosen class with everything chosen so far (seats, meet & greet); the same question as the tile is answered from memory
  const finalKey = JSON.stringify([quoteKey, selected, mode === MODES.FAMILY ? seats : 0, !!(pickupAirport && meet)]);
  const askFinal = useCallback(async (id) => {
    const res = await quoteClient.quote(quoteBody({ cls, mode, hours, seats, pickup, dropoff, when, meetGreet: !!(pickupAirport && meet), pickupAirport, dropoffAirport }));
    if (finalId.current !== id) return;
    if (res.cancelled) return;
    if (res.ok) { setFinalQuote(res.quote); setFinalState('ok'); setFinalMessage(''); } else { setFinalQuote(null); setFinalState('error'); setFinalMessage(res.message || UNAVAILABLE); }
  // eslint-disable-next-line
  }, [finalKey]);
  useEffect(() => {
    if (!cls || !readyForClasses({ mode, pickup, dropoff })) { setFinalQuote(null); setFinalState('idle'); return undefined; }
    const id = ++finalId.current;
    setFinalState('loading');
    const t = setTimeout(() => askFinal(id), 250);
    return () => clearTimeout(t);
  // eslint-disable-next-line
  }, [finalKey]);

  // gentle scrolling to each new step
  const scrollTo = (ref) => { if (!wide && ref.current && ref.current.scrollIntoView && !reduced()) ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  useEffect(() => { if (sections.classes) setTimeout(() => scrollTo(classesRef), 250); }, [sections.classes]);       // eslint-disable-line
  useEffect(() => { if (selected) setTimeout(() => scrollTo(detailsRef), 300); }, [selected]);                       // eslint-disable-line
  useEffect(() => { if (meet && !wide) setTimeout(() => { const el = document.querySelector('[data-testid=sign-preview]'); if (el && el.scrollIntoView && !reduced()) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 350); }, [meet, wide]);
  useEffect(() => { if (selected && !list.some((c) => c.id === selected)) setSelected(''); }, [list, selected]);

  const setPlace = (setter) => (v) => setter({ address: v, lat: null, lng: null, committed: false });
  const pickPlace = (setter) => (sel) => setter({ address: sel.address, lat: sel.lat, lng: sel.lng, committed: true });
  const commitText = (place, setter) => () => setTimeout(() => setter((p) => (p.lat == null && p.address.trim().length >= 4 ? { ...p, committed: true } : p)), 600);

  const dropNoteParts = splitDropoffNote(dropNote);
  const chosenQuote = finalQuote;
  const total = quoteTotal(chosenQuote);
  const canConfirm = !busy && finalState === 'ok' && !!cls && total > 0;

  const confirm = async () => {
    setFormError('');
    const fail = (title, description) => { setFormError(description); toast({ title, description, variant: 'destructive' }); };
    if (!pickup.address.trim()) return fail('Missing pickup', 'Please enter a pickup address.');
    if (!isHourly && !dropoff.address.trim()) return fail('Missing drop-off', 'Please enter where you are going.');
    if (!cls) return fail('Choose a class', 'Please choose your car.');
    if (!isHourly) {
      const [a, b] = [await GoogleMapsService.validateTriStateArea(pickup.address), await GoogleMapsService.validateTriStateArea(dropoff.address)];
      if (!a.isTriState && !b.isTriState) return fail('Service area', 'Your ride must either start or end in the Tri-State Area (NY, NJ, CT).');
    }
    if (when) {
      const d = new Date(`${when.date}T${when.time}:00`);
      if (Number.isNaN(d.getTime())) return fail('Date and time', 'Please choose the pickup date and time.');
    }
    if (business) { if (!savedT && !(travelerId === 'new' && tidyName(newT.name))) return fail('Traveler', 'Choose who is riding, or add a new traveler.'); if (!savedT && newT.mobile.trim()) { const mp = phoneProblem(newT.mobile); if (mp) return fail('Traveler', mp); } }
    if (!business && someoneElse) { const pp = passengerProblem(paxName) || phoneProblem(paxPhone); if (pp) return fail("Who's riding", pp); }
    if (pickupAirport && meet) { const pr = signNameProblem(signName == null ? signDefault : signName); if (pr) return fail('Name on sign', pr); }
    { const np = noteProblem(riderNote); if (np) return fail('Note to your chauffeur', np); }
    setBusy(true);
    try {
      const body = checkoutBody({
        cls, mode, hours, seats, pickup, dropoff, when, now: nyNow(), user: currentUser, preferences: prefsForBooking(prefs), total, origin: window.location.origin, riderNote, noteChips,
        airport: { pickup: pickupAirport, dropoff: dropoffAirport },
        party: {
          passengers, bags, flightNumber, terminal, meetGreet: !!(pickupAirport && meet), signName: capitalizeName(tidyName(signName == null ? signDefault : signName)),
          dropTerminal: dropNoteParts.terminal, dropAirline: dropNoteParts.airline, someoneElse, passengerName: capitalizeName(tidyName(paxName)), passengerPhone: paxPhone.trim(),
        },
      });
      if (business) {
        const traveler = savedT ? { id: savedT.id } : { name: capitalizeName(tidyName(newT.name)), mobile: newT.mobile.trim(), email: newT.email.trim(), save: newT.save };
        await bizApi('/business/rides', { method: 'POST', body: businessBody(body, { when, traveler }) });
        toast({ title: 'Ride booked', description: 'It is on your company account.', duration: 3000 });
        navigate('/business/rides');
        return;
      }
      const res = await fetch(`${API}/api/payments/create-checkout-session`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token() ? { Authorization: `Bearer ${token()}` } : {}) }, body: JSON.stringify(body) });
      if (res.status === 401) throw Object.assign(new Error('Please sign in again to continue.'), { userMessage: 'Please sign in again to continue.' });
      if (!res.ok) {
        let reason = '';
        if (res.status === 422 || res.status === 402) { const j = await res.json().catch(() => null); const d = j && j.detail; reason = typeof d === 'string' ? d : (d && d.message) || ''; }
        throw Object.assign(new Error(reason || 'Failed to create payment session'), { userMessage: reason });
      }
      const { url } = await res.json();
      toast({ title: 'Redirecting to payment', description: `Total: ${money(total)}. You'll be redirected to secure payment.`, duration: 3000 });
      window.location.href = url;
    } catch (e) {
      fail('Booking failed', e.userMessage || (business && e.message) || 'Failed to book ride. Please try again.');
    } finally { setBusy(false); }
  };

  const allErrored = list.length > 0 && list.every((c) => quotes[c.id] && quotes[c.id].status === 'error');
  const classError = allErrored ? quotes[list[0].id].message : '';

  const recapEl = sections.details && cls && !wide ? (
    <div className="text-base text-gray-100 space-y-1.5 rounded-3xl border border-white/15 bg-white/[0.03] p-5" data-testid="summary">
      <p className="text-sm tracking-[0.14em] uppercase text-gray-300 mb-2">Your trip</p>
      <p><span className="text-gray-400">From</span> {pickup.address}</p>
      <p><span className="text-gray-400">{isHourly ? 'For' : 'To'}</span> {isHourly ? `${effectiveHours(hours, cls)} hours` : dropoff.address}</p>
      {someoneElse && tidyName(paxName) && <p><span className="text-gray-400">Passenger</span> {capitalizeName(tidyName(paxName))}</p>}
    </div>
  ) : null;
  // every line comes from the server quote (fare lines, then taxes and fees); the page adds nothing up
  const breakdownLines = quoteBreakdown(finalQuote);
  const totalEl = sections.details && cls ? (
    <div className="rounded-3xl border border-white/15 bg-[#15171b]" data-testid="total-block">
      <button type="button" data-testid="total-toggle" aria-expanded={showBreakdown} onClick={() => setShowBreakdown((v) => !v)} disabled={finalState !== 'ok'}
        className="w-full flex items-center justify-between px-5 py-4 rounded-3xl text-left hover:bg-white/[0.04] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/60 disabled:cursor-default">
        <span className="text-base text-gray-300 flex items-center gap-2">Total <ChevronDown className={`h-4 w-4 text-gray-300 transition-transform duration-300 ${showBreakdown ? 'rotate-180' : ''}`} aria-hidden="true" /></span>
        <span className="text-3xl font-semibold tracking-tight text-gray-50" data-testid="summary-total">{finalState === 'ok' ? money(total) : '…'}</span>
      </button>
      {showBreakdown && finalState === 'ok' && (
        <div className="px-5 pb-5 pt-1 max-h-[40vh] overflow-y-auto" data-testid="breakdown">
          <ul className="divide-y divide-white/10 text-base">
            {breakdownLines.map((l, i) => (
              <li key={`${l.label}-${i}`} className="flex items-baseline justify-between gap-4 py-2.5"><span className="text-gray-200">{l.label}</span><span className="text-gray-100 tabular-nums">{money(l.amount)}</span></li>
            ))}
          </ul>
          <p className="text-sm text-gray-400 mt-3">Tolls are included in the fare. Gratuity is not included.</p>
        </div>
      )}
    </div>
  ) : null;
  const confirmEl = (
    <div data-testid="confirm-block" className="space-y-3">
      {formError && <p className="text-base text-red-300" data-testid="form-error">{formError}</p>}
      {sections.details && finalState === 'error' && (
        <p className="text-base text-amber-300" data-testid="final-error">{finalMessage} <button type="button" data-testid="final-retry" onClick={() => { const id = ++finalId.current; setFinalState('loading'); askFinal(id); }} className="underline underline-offset-4 ml-1 hover:text-amber-200">Try again</button></p>
      )}
      {totalEl}
      <button type="button" data-testid="confirm" disabled={!canConfirm} onClick={confirm}
              className="w-full h-16 rounded-full text-xl font-bold text-neutral-950 bg-orange-500 hover:bg-orange-400 disabled:opacity-40 active:scale-[0.99] transition-all flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : finalState === 'loading' ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Updating price…</> : finalState === 'ok' ? `Confirm · ${money(total)}` : 'Confirm'}
            </button>
            {policy && <p className="text-sm text-gray-400 text-center leading-relaxed" data-testid="policy-line">{policy.text}</p>}
    </div>
  );
  const heroTextEl = (<>
        {/* 1. Where */}
        <Reveal>
          <h1 data-testid="greeting" className="text-4xl sm:text-5xl lg:text-4xl font-semibold tracking-tight leading-[1.05] pt-2" style={{ textShadow: '0 2px 18px rgba(0,0,0,0.65)' }}>{greetingText(greetHour, capitalizeName(currentUser && currentUser.firstName))}</h1>
          <p className="text-lg sm:text-xl lg:text-lg text-gray-100 pt-2 pb-6 lg:pb-5" style={{ textShadow: '0 2px 14px rgba(0,0,0,0.65)' }}>Where can we take you?</p>
        </Reveal>

        <Reveal delay={120}>
          <div className="rounded-3xl border border-white/30 bg-black/45 backdrop-blur-md px-6 grid grid-cols-1 md:grid-cols-2 md:gap-6 divide-y divide-white/20 md:divide-y-0" data-testid="where">
            <div className="py-1">
              <GooglePlacesInput value={pickup.address} onChange={setPlace(setPickup)} onSelect={pickPlace(setPickup)} onBlurText={commitText(pickup, setPickup)} placeholder="Pickup" className={fieldCls} inputTestId="preview-pickup" />
            </div>
            {isHourly ? (
              <div className="py-3 flex items-center justify-between md:border-l md:border-white/20 md:pl-6" data-testid="hours-picker">
                <span className="text-lg sm:text-xl text-gray-200">Hours</span>
                <span className="inline-flex items-center gap-3 rounded-full border border-white/20 p-1">
                  <button type="button" aria-label="Fewer hours" disabled={hours <= HOURS_MIN} onClick={() => setHours((h) => Math.max(HOURS_MIN, h - 1))} className={roundBtn}>−</button>
                  <span className="text-xl font-semibold min-w-[2rem] text-center tabular-nums" data-testid="hours-value">{hours}</span>
                  <button type="button" aria-label="More hours" disabled={hours >= HOURS_MAX} onClick={() => setHours((h) => Math.min(HOURS_MAX, h + 1))} className={roundBtn}>+</button>
                </span>
              </div>
            ) : (
              <div className="py-1 md:border-l md:border-white/20 md:pl-6">
                <div className={`rounded-2xl border px-3 -mx-3 transition-colors duration-300 ${nextIsWhere ? 'border-orange-400/60' : 'border-transparent'}`} data-testid="where-to-box" data-highlight={nextIsWhere ? 'true' : 'false'}>
                  <GooglePlacesInput value={dropoff.address} onChange={setPlace(setDropoff)} onSelect={pickPlace(setDropoff)} onBlurText={commitText(dropoff, setDropoff)} placeholder={nextIsWhere ? 'Where are you going?' : 'Where to?'} className={fieldCls} inputTestId="preview-dropoff" />
                </div>
              </div>
            )}
          </div>
        </Reveal>

        <Reveal delay={200} className="mt-4">
          <p className="text-base text-gray-100 leading-relaxed" style={{ textShadow: '0 1px 12px rgba(0,0,0,0.8)' }} data-testid="mode-line">
            {mode === MODES.RIDE ? (
              <>Need a chauffeur for a few hours? <button type="button" data-testid="mode-hourly" onClick={() => setMode(MODES.HOURLY)} className="text-white underline underline-offset-4 decoration-white/50 hover:decoration-white">By the hour</button>
                {' · '}Traveling with kids? <button type="button" data-testid="mode-family" onClick={() => setMode(MODES.FAMILY)} className="text-white underline underline-offset-4 decoration-white/50 hover:decoration-white">Little Buraq</button></>
            ) : (
              <>{mode === MODES.HOURLY ? 'By the hour' : 'Little Buraq'} · <button type="button" data-testid="mode-ride" onClick={() => setMode(MODES.RIDE)} className="text-white underline underline-offset-4 decoration-white/50 hover:decoration-white">Back to a ride</button></>
            )}
          </p>
        </Reveal>

  </>);
  const belowHeroEl = (<>

        <Reveal delay={220} className="mt-2">
          <div className="flex items-center gap-3">
            <button type="button" data-testid="when-chip" onClick={() => setShowWhen((v) => !v)} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 h-12 text-base text-gray-50 hover:border-white/70">
              {whenLabel(when)} <ChevronDown className={`h-4 w-4 transition-transform ${showWhen ? 'rotate-180' : ''}`} />
            </button>
            {when && <button type="button" onClick={() => { setWhen(null); setWhenDate(null); }} className="text-sm text-gray-400 hover:text-white">Back to now</button>}
          </div>
          {showWhen && (
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-3 inline-block" data-testid="when-panel">
              <DatePicker inline selected={whenDate} onChange={(d) => { setWhenDate(d); setWhen(partsFromPicker(d)); }} showTimeSelect timeIntervals={15} minDate={new Date()} dateFormat="MMM d • h:mm aa" />
              <div className="flex justify-between mt-2 px-1">
                <button type="button" className="text-sm text-gray-400 hover:text-white" onClick={() => { setWhen(null); setWhenDate(null); setShowWhen(false); }}>Now</button>
                <button type="button" className="text-sm text-gray-300 hover:text-white" onClick={() => setShowWhen(false)}>Done</button>
              </div>
            </div>
          )}
        </Reveal>



  </>);
  const airportEl = (<>
        {/* 2. Airport details */}
        {pickupAirport && (
          <Reveal className="mt-8 lg:mt-8" delay={0}>
            <div className="rb-divider mb-8" aria-hidden="true" />
            <div className="space-y-4" data-testid="airport-pickup">
              <p className="flex items-center gap-2 text-sm text-gray-400"><Plane className="h-4 w-4 text-gray-300" /> Pickup at <span className="text-white">{pickupAirport.name}</span></p>
              <input data-testid="flight-number" value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)} placeholder="Flight number" className={lineInput} autoComplete="off" />
              {flightState === 'looking' && <p className="text-sm text-gray-400 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Checking your flight…</p>}
              {flightState === 'found' && terminalFromFlight && <p className="text-sm text-emerald-400 flex items-center gap-2" data-testid="terminal-from-flight"><Check className="h-4 w-4" /> Arriving at {terminalName(pickupAirport, terminal)}</p>}
              {flightState === 'unavailable' && <p className="text-sm text-amber-300" data-testid="flight-unavailable">We couldn't check flights right now — choose your terminal</p>}
              {flightState === 'noterminal' && <p className="text-sm text-amber-300" data-testid="flight-no-terminal">We found your flight but it has no terminal yet — choose your terminal</p>}
              {flightState === 'missing' && <p className="text-sm text-amber-300" data-testid="flight-not-found">We couldn't find that flight — choose your terminal</p>}
              {pickupAirport.terminals.length > 0 && !showChips && flightState !== 'found' && (
                <button type="button" onClick={() => setShowChips(true)} className="text-sm text-gray-400 underline underline-offset-4 decoration-white/40 hover:text-white">No flight number yet? Choose your terminal</button>
              )}
              {pickupAirport.terminals.length > 0 && (showChips || flightState === 'found') && (
                <div className="flex flex-wrap gap-2" data-testid="pickup-terminals">
                  {pickupAirport.terminals.map((t) => (
                    <button type="button" key={t.code} onClick={() => { setTerminal(terminal === t.code ? '' : t.code); setTerminalFromFlight(false); }}
                      className={`px-5 h-12 rounded-full text-base border ${terminal === t.code ? 'bg-orange-500 text-neutral-950 font-semibold border-orange-500' : 'border-white/15 text-gray-300 hover:border-white/70'}`}>{t.name}</button>
                  ))}
                </div>
              )}
            </div>
          </Reveal>
        )}
        {dropoffAirport && (
          <Reveal className="mt-10">
            <div className="rb-divider mb-8" aria-hidden="true" />
            <div className="space-y-3" data-testid="airport-dropoff">
              <p className="flex items-center gap-2 text-sm text-gray-400"><Plane className="h-4 w-4 text-gray-300" /> Drop-off at <span className="text-white">{dropoffAirport.name}</span></p>
              <input data-testid="dropoff-note" value={dropNote} maxLength={60} onChange={(e) => setDropNote(e.target.value)} placeholder="Airline or terminal" className={lineInput} />
            </div>
          </Reveal>
        )}


  </>);
  const classesEl = (<>
        {/* 3. Cars, with real prices */}
        {sections.classes && (
          <section className={wide ? '' : 'mt-14'} data-testid="classes">
            {!wide && <div className="rb-divider mb-12" aria-hidden="true" />}
            <Reveal><h2 className="text-3xl font-semibold tracking-tight mb-2 text-gray-50">Choose your car</h2><p className="text-sm text-gray-300 mb-6">{isHourly ? `Prices for ${hours} ${hours === 1 ? 'hour' : 'hours'} or the class minimum.` : 'The price you see is the price you pay, taxes and tolls included.'}</p></Reveal>
            {classError && <p className="text-sm text-amber-300 mb-4" data-testid="class-error">{classError}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="class-grid">
              {list.map((c, i) => {
                const q = quotes[c.id] || { status: 'loading' };
                const active = selected === c.id;
                return (
                  <Reveal key={`${mode}-${c.id}`} delay={i * 150} className="h-full">
                    <div className="flex flex-col h-full">
                    <button type="button" data-testid={`class-${c.id}`} disabled={q.status !== 'ok'} onClick={() => setSelected(c.id)}
                      className={`group w-full flex-1 text-left rounded-3xl border overflow-hidden transition-all duration-300 hover:-translate-y-1 ${active ? 'border-orange-500 bg-white/[0.06] shadow-[0_0_36px_-10px_rgba(249,115,22,0.55)]' : 'border-white/15 bg-white/[0.04] hover:border-white/35'} ${q.status === 'error' ? 'opacity-60 cursor-not-allowed' : ''} ${q.status === 'loading' ? 'cursor-wait' : ''} focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/60`}>
                      <span className="block w-full aspect-video lg:aspect-auto lg:h-56 bg-gradient-to-b from-white/[0.08] to-white/[0.02]">
                        <img src={c.image} alt={`${c.name}: ${c.carModels}`} loading="lazy" className="w-full h-full object-cover" data-testid={`photo-${c.id}`} />
                      </span>
                      <span className="block p-5">
                        <span className="block text-2xl font-semibold text-gray-50" data-testid={`name-${c.id}`}>{c.name}</span>
                        <span className="block mt-0.5" data-testid={`price-${c.id}`}>
                          {q.status === 'ok' && <span className="text-lg text-gray-200">{money(quoteTotal(q.quote))}</span>}
                          {q.status === 'loading' && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
                          {q.status === 'error' && <span className="text-sm text-amber-300" data-testid={`price-error-${c.id}`}>{q.retryable === false ? 'Unavailable' : UNAVAILABLE}</span>}
                        </span>
                        <span className="block text-base text-gray-300 mt-2">{c.description}</span>
                        <span className="mt-2 flex items-center gap-4 text-sm text-gray-300"><span className="flex items-center gap-1.5"><Users className="h-4 w-4" /> Up to {c.maxPassengers}</span><span className="flex items-center gap-1.5"><Luggage className="h-4 w-4" /> {c.maxBags} bags</span></span>
                      </span>
                    </button>
                    {q.status === 'error' && q.retryable !== false && (
                      <button type="button" data-testid={`retry-${c.id}`} onClick={() => retryTile(c)} className="mt-3 self-start inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/25 text-sm text-gray-100 hover:border-white/60 hover:bg-white/[0.06] transition-colors">Try again</button>
                    )}
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </section>
        )}


  </>);
  const detailsEl = (<>
        {/* 4. Details after a class is picked */}
        {sections.details && (
          <section className={`${wide ? 'space-y-8 mt-8' : 'space-y-12 mt-16'}`} data-testid="details">
            <div className="rb-divider" aria-hidden="true" />
            {sections.seats && (
              <Reveal><div data-testid="seats-step"><p className="text-sm text-gray-400 mb-3">Child seats <span className="text-gray-400">· $15 each, fitted and inspected</span></p><Stepper label="Seats" value={seats} min={1} max={SEAT_MAX} onChange={setSeats} testid="seats" /></div></Reveal>
            )}

            {sections.style && (
              <Reveal delay={80}>
                <div data-testid="pickup-style" className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button type="button" data-testid="style-curbside" onClick={() => setMeet(false)} className={`text-left rounded-2xl border p-5 ${!meet ? 'border-orange-500 bg-orange-500/[0.08]' : 'border-white/10 bg-white/[0.03]'}`}>
                      <span className="block font-medium">Curbside</span>
                      <span className="block text-sm text-gray-400 mt-1">Usually faster: your chauffeur pulls up as soon as you're outside with your bags.</span>
                    </button>
                    {pickupAirport.meetGreet && pickupAirport.meetGreet.available && (
                      <button type="button" data-testid="style-meet-greet" onClick={() => setMeet(true)} className={`text-left rounded-2xl border p-5 ${meet ? 'border-orange-500 bg-orange-500/[0.08]' : 'border-white/10 bg-white/[0.03]'}`}>
                        <span className="block font-medium">Meet &amp; greet {Number(pickupAirport.meetGreet.price) > 0 ? <span className="text-white font-semibold">+{money(pickupAirport.meetGreet.price)}</span> : <span className="text-gray-300">included</span>}</span>
                        <span className="block text-sm text-gray-400 mt-1">Your chauffeur waits inside arrivals with a sign with your name.</span>
                      </button>
                    )}
                  </div>
                  {meet && <MeetGreetPanel calm lat={pickup.lat} lng={pickup.lng} terminal={terminal} flightNumber={flightNumber} flightDate={lookupDate} defaultName={signDefault} signName={signName} onSignName={setSignName} />}
                </div>
              </Reveal>
            )}

            {business && (
              <Reveal delay={160}>
                <div data-testid="business-traveler" className="space-y-4">
                  <label className="block text-sm tracking-[0.14em] uppercase text-gray-300" htmlFor="traveler-select">Who is riding</label>
                  <select id="traveler-select" data-testid="traveler-select" value={travelerId} onChange={(e) => setTravelerId(e.target.value)} className="w-full h-12 rounded-full bg-[#15171b] border border-white/20 px-5 text-base text-gray-50">
                    <option value="" disabled>Choose a traveler</option>
                    {business.travelers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    <option value="new">New traveler…</option>
                  </select>
                  {savedT && <p className="text-sm text-gray-400" data-testid="traveler-summary">{[savedT.mobile, savedT.email].filter(Boolean).join(' · ')}</p>}
                  {travelerId === 'new' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input data-testid="new-traveler-name" value={newT.name} maxLength={80} onChange={(e) => setNewT({ ...newT, name: e.target.value })} placeholder="Traveler's name" className={lineInput} />
                      <input data-testid="new-traveler-mobile" value={newT.mobile} maxLength={40} inputMode="tel" onChange={(e) => setNewT({ ...newT, mobile: e.target.value })} placeholder="Mobile" className={lineInput} />
                      <input data-testid="new-traveler-email" type="email" value={newT.email} maxLength={200} onChange={(e) => setNewT({ ...newT, email: e.target.value })} placeholder="Email (optional)" className={`${lineInput} sm:col-span-2`} />
                      <label className="sm:col-span-2 flex items-center gap-3 text-base text-gray-100 min-h-[44px]"><input type="checkbox" checked={newT.save} onChange={(e) => setNewT({ ...newT, save: e.target.checked })} className="h-5 w-5 accent-white" /> Save to my travelers</label>
                    </div>
                  )}
                </div>
              </Reveal>
            )}
            {!business && <Reveal delay={160}>
              <div data-testid="whos-riding" className="space-y-4">
                <div className="inline-flex rounded-full border border-white/10 overflow-hidden text-sm">
                  {[[false, 'For me'], [true, 'Someone else']].map(([v, label]) => (
                    <button type="button" key={label} data-testid={v ? 'riding-other' : 'riding-me'} onClick={() => setSomeoneElse(v)} className={`px-6 h-12 text-base ${someoneElse === v ? 'bg-orange-500 text-neutral-950 font-semibold' : 'text-gray-400 hover:text-white'}`}>{label}</button>
                  ))}
                </div>
                {someoneElse && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input data-testid="passenger-name" value={paxName} maxLength={80} onChange={(e) => setPaxName(e.target.value)} placeholder="Passenger's name" className={lineInput} />
                    <input data-testid="passenger-phone" value={paxPhone} maxLength={40} inputMode="tel" onChange={(e) => setPaxPhone(e.target.value)} placeholder="Phone (optional)" className={lineInput} />
                    <p className="sm:col-span-2 text-sm text-gray-400">Your chauffeur can call them through RideBuraq without seeing the number.</p>
                  </div>
                )}
              </div>
            </Reveal>}

            <Reveal delay={240}>
              <div className="space-y-3" data-testid="party">
                <Stepper label="Passengers" icon={Users} value={passengers} min={1} max={party.passengers} onChange={setPassengers} testid="passengers" />
                <Stepper label="Bags" icon={Luggage} value={bags} min={0} max={party.bags} onChange={setBags} testid="bags" />
                {better && keepFit !== fitKey && (
                  <div className="rounded-3xl border border-white/20 bg-white/[0.05] p-5" data-testid="fit-suggestion" role="status">
                    <p className="text-base text-gray-50 leading-relaxed" data-testid="fit-message">{fitMessage(cls, better, passengers, bags)}</p>
                    <div className="flex gap-3 mt-4">
                      <button type="button" data-testid="fit-switch" aria-label={`Switch to ${better.name}`} onClick={() => { setSelected(better.id); setKeepFit(''); }} className="px-6 h-12 rounded-full text-base font-semibold bg-white text-neutral-950 hover:bg-gray-200 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70">Switch</button>
                      <button type="button" data-testid="fit-keep" aria-label={`Keep ${cls.name}`} onClick={() => setKeepFit(fitKey)} className="px-6 h-12 rounded-full text-base border border-white/25 text-gray-100 hover:border-white/60 hover:bg-white/[0.06] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/70">Keep</button>
                    </div>
                  </div>
                )}
              </div>
            </Reveal>

            <Reveal delay={320}>
              <PreferencesCard prefs={prefs} setPrefs={setPrefs} />
            </Reveal>

            <Reveal delay={360}>
              <RiderNoteCard note={riderNote} setNote={setRiderNote} chips={noteChips} setChips={setNoteChips} />
            </Reveal>

            <Reveal delay={400}>
              <OurStandards variant="short" />
            </Reveal>
          </section>
        )}

  </>);

  return (
    <div className="min-h-screen bg-[#0d0e11] text-gray-50 relative" style={{ overflowX: 'clip' }} data-testid="book-preview" data-layout={wide ? 'wide' : 'narrow'}>
      {/* HERO: the photo sits behind the greeting and the address fields only */}
      <section className="relative" data-testid="hero">
        <HeroBackdrop phone={phone} />
        {/* the Back button in the far top-left corner and the logo in the far top-right corner of the page */}
        <header className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-4 sm:px-6 pt-4 sm:pt-5" data-testid="top-corners">
          <button type="button" onClick={() => navigate('/dashboard')} aria-label="Back" className="w-11 h-11 rounded-full border border-white/20 bg-black/30 backdrop-blur-sm flex items-center justify-center text-gray-100 hover:text-white hover:border-white/60 transition-colors">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => navigate('/')} aria-label="Home" className="flex items-center gap-2.5">
            <img src={LOGO} alt="RideBuraq" className="w-9 h-9 object-contain" />
            <span className="text-xl font-bold tracking-wide" style={{ textShadow: '0 1px 10px rgba(0,0,0,0.8)' }}>Ride<span className="text-white">Buraq</span></span>
          </button>
        </header>
        {/* the greeting and the address row sit low, where the photo begins to fade into the page colour */}
        <div className={`relative w-full mx-auto px-6 sm:px-10 ${wide ? 'max-w-[1400px] lg:px-14' : 'max-w-3xl'} pt-28 pb-10 md:pt-40 md:pb-12 flex flex-col justify-end min-h-[520px] md:min-h-[480px] lg:min-h-[540px]`} data-testid="hero-body">
          <div data-testid="hero-content">
            {heroTextEl}
          </div>
        </div>
      </section>

      <div className="rb-ambient" data-testid="ambient">
      <div className="rb-glows" aria-hidden="true">
        {['a', 'b', 'c', 'd', 'e'].map((k) => <div key={k} className={`rb-glow rb-glow-${k}`} />)}
      </div>
      <div className={`relative w-full mx-auto px-6 sm:px-10 ${wide ? 'max-w-[1400px] lg:px-14 pb-12' : 'max-w-3xl pb-10'}`}>
        {wide ? (
          <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-14 items-start" data-testid="two-columns">
            <div data-testid="left-column">
              {belowHeroEl}
              {airportEl}
              {detailsEl}
            </div>
            <div className="sticky top-4 h-[calc(100vh-2rem)] flex flex-col gap-4" data-testid="right-column">
              <div className="min-h-0 flex-1 overflow-y-auto pr-1">{sections.classes && classesEl}</div>
              {sections.details && <div className="shrink-0" data-testid="pinned">{confirmEl}</div>}
            </div>
          </div>
        ) : (
          <div data-testid="one-column">
            {belowHeroEl}
            {airportEl}
            <div ref={classesRef} className="scroll-mt-6" />
            {sections.classes && classesEl}
            <div ref={detailsRef} className="scroll-mt-6" />
            {detailsEl}
            {recapEl && <div className="mt-10">{recapEl}</div>}
          </div>
        )}
      </div>
      <div className={`relative w-full mx-auto px-6 sm:px-10 ${wide ? 'max-w-[1400px] lg:px-14' : 'max-w-3xl'} ${!wide && sections.details ? 'pb-64' : ''}`}><PreviewFooter /></div>
      </div>

      {/* Phone and tablet: the price and Confirm stay at the bottom; the page has room under the last item so nothing hides behind them */}
      {!wide && sections.details && (
        <div className="fixed bottom-0 inset-x-0 z-20 pt-10 pb-5 px-5" style={{ background: 'linear-gradient(180deg, rgba(13,14,17,0) 0px, #0d0e11 40px)' }} data-testid="confirm-bar">
          <div className="max-w-3xl mx-auto" data-testid="confirm-bar-inner">{confirmEl}</div>
        </div>
      )}
    </div>
  );
};

export default BookPreview;
